import { useMemo } from 'react';
import { Box, Chip, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useAppState } from '../../context/LoadTestContext';
import { useUnits } from '../units';
import { LVDT_COLORS } from '../graphs/LvdtGraph';
import { TEST_LOADS } from '../calibrationSettings';
import { TEST_STATUS } from '../steps';
import { PLATE_ANGLE_COLORS, PLATE_ANGLE_CRITICAL_DEG, PLATE_ANGLE_WARNING_DEG, plateAngleLevel } from '../plateAngle';

// The new dashboard's readings column. Every value here comes from the
// get-lt-data payload (the classic footer's settlement, rate and pressure are
// still fixed sample numbers); a reading the logger doesn't send shows "—".
// The design, test and max loads are entered in the Calibration window and
// arrive with the calibration the payload was converted with. Each carries a
// sample of its line on the load graph (same colour and dash).
// A full LVDT bar is this much travel (inches, as the readings arrive), so the
// bars read as how far each LVDT has moved rather than against each other.
const LVDT_FULL_SCALE_IN = 2;

function Stat({ label, value, unit, loading }) {
  return (
    <Box sx={{ p: 1.25, borderRadius: 1, backgroundColor: 'background.dashboard', minWidth: 0 }}>
      <Typography variant='caption' color='text.secondary' noWrap component='div'>{label}</Typography>
      {loading ? (
        <Skeleton variant='text' animation='wave' sx={{ fontSize: 22, width: '70%' }} />
      ) : (
        <Typography sx={{ fontSize: 22, fontWeight: 600, lineHeight: 1.3 }} noWrap>
          {value}
          {unit && value !== '—' && (
            <Typography component='span' variant='caption' color='text.secondary' sx={{ ml: 0.5 }}>{unit}</Typography>
          )}
        </Typography>
      )}
    </Box>
  );
}

function ReadingsPanel({ box, selectedStep }) {
  const { loading, currentReadings, loadGraphData, activeStrainGauges, calibration, testStatus } = useAppState();
  // Where the test stands (in progress, back at zero, complete...), beside the
  // logger's own status.
  const test = TEST_STATUS[testStatus];
  const units = useUnits();
  const theme = useTheme();
  const lvdtColors = LVDT_COLORS[theme.palette.mode] || LVDT_COLORS.light;
  // The plate angle in orange or red, with an icon (so it doesn't rely on
  // colour alone) and a hover message saying which level it has reached.
  const plateLevel = plateAngleLevel(currentReadings?.plate_angle_deg);
  const plateAlert = plateLevel && {
    color: PLATE_ANGLE_COLORS[plateLevel],
    Icon: plateLevel === 'critical' ? ErrorOutlineIcon : WarningAmberIcon,
    label: plateLevel === 'critical' ? 'Plate angle critical' : 'Plate angle warning',
    message: plateLevel === 'critical'
      ? `Plate angle is ${PLATE_ANGLE_CRITICAL_DEG.toFixed(1)}° or more (critical)`
      : `Plate angle is ${PLATE_ANGLE_WARNING_DEG.toFixed(1)}° or more (warning; critical from ${PLATE_ANGLE_CRITICAL_DEG.toFixed(1)}°)`,
  };

  const fmt = (value, formatter) => (value === null || value === undefined || Number.isNaN(Number(value)) ? '—' : formatter(Number(value)));

  const peakLoad = useMemo(() => {
    const rows = Array.isArray(loadGraphData) ? loadGraphData : [];
    const loads = rows.map((r) => Number(r?.Loadcell_kips)).filter(Number.isFinite);
    return loads.length ? Math.max(...loads) : null;
  }, [loadGraphData]);

  const lvdts = [1, 2, 3, 4].map((channel) => ({
    channel,
    value: currentReadings?.[`LVDT${channel}`],
    display: units.forSensor('lvdt', channel),
  }));

  const settlementDisplay = units.forSensor('settlement')
  const rawSettlement = settlementDisplay.sourceUnit === 'mV'

  const status = box?.online ? (box?.writing ? 'Writing' : 'Online') : 'Offline';
  const last = box?.LastReadingDate ? new Date(box.LastReadingDate) : null;
  const statusLabel = status + (!box?.online && last && !Number.isNaN(last.getTime())
    ? ` · ${last.toLocaleDateString([], { month: 'short', day: 'numeric' })}` : '');

  return (
    <Paper component='aside' aria-label='Readings' sx={{ p: 1.5, borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Stack direction='row' alignItems='center' flexWrap='wrap' useFlexGap columnGap={1} rowGap={0.5}>
        <Typography variant='subtitle1' component='h2' fontWeight={600} sx={{ mr: 'auto' }}>Readings</Typography>
        {/* The two chips wrap together, so a narrow column doesn't split them. */}
        <Stack direction='row' spacing={0.5} sx={{ ml: 'auto' }}>
          {!loading && test && (
            <Tooltip arrow title={test.description || ''}>
              <Chip size='small' variant='outlined' color={test.color} label={test.label.toLowerCase() !== 'missing sensors' ? `Test ${test.label.toLowerCase()}` : test.label} sx={{ fontWeight: 500 }} />
            </Tooltip>
          )}
          {/* The logger's own status; a past test has no box on screen. */}
          {box && <Chip size='small' label={statusLabel} color={box?.online ? 'success' : 'default'} variant={box?.online ? 'filled' : 'outlined'} />}
        </Stack>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
        <Stat loading={loading} label='Load now' value={fmt(currentReadings?.Loadcell, units.fmtForce)} unit={units.forceUnit} />
        <Stat loading={loading} label='Peak Load' value={fmt(peakLoad, units.fmtForce)} unit={units.forceUnit} />
        <Stat loading={loading} label={rawSettlement ? 'Avg LVDT reading' : 'Avg settlement'} value={fmt(currentReadings?.deflection, settlementDisplay.format)} unit={units.lengthUnit} />
        {/* The payload carries no converted pressure yet. */}
        <Stat loading={loading} label='Pressure' value='—' unit={units.pressureUnit} />
      </Box>

      <Box>
        <Typography variant='caption' color='text.secondary'>Test loads ({units.forceUnit})</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1, mt: 0.5 }}>
          {TEST_LOADS.map(({ key, label, short, color, dash }) => (
            <Box key={key} sx={{ minWidth: 0 }} aria-label={label}>
              <Stack direction='row' alignItems='center' spacing={0.75} sx={{ minWidth: 0 }}>
                <svg width='14' height='4' viewBox='0 0 14 4' aria-hidden='true' focusable='false' style={{ flexShrink: 0 }}>
                  <line x1='0' y1='2' x2='14' y2='2' stroke={theme.palette[color].main} strokeWidth='2' strokeDasharray={dash} />
                </svg>
                <Typography variant='body2' noWrap>{short}</Typography>
              </Stack>
              <Typography variant='body2' fontWeight={500} noWrap sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {loading ? '—' : fmt(calibration?.loads?.[key], units.fmtForce)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box>
        {/* <Typography variant='caption' color='text.secondary'>LVDTs ({units.lengthUnit})</Typography> */}
        <Typography variant='caption' color='text.secondary'>LVDTs</Typography>
        <Stack spacing={0.75} sx={{ mt: 0.5 }}>
          {lvdts.map(({ channel, value, display }) => (
            <Stack
              key={channel}
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              spacing={1}
            >
              <Stack direction='row' alignItems='center' spacing={0.75}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: lvdtColors[channel - 1],
                  }}
                />
                <Typography variant='body2'>
                  LVDT {channel}
                </Typography>
              </Stack>

              <Typography variant='body2' sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {loading ? '—' : display.format(value)}
                {display.unit !== 'unknown' && (
                  <Typography
                    component='span'
                    variant='caption'
                    color='text.secondary'
                    sx={{ ml: 0.5 }}
                  >
                    {display.unit}
                  </Typography>
                )}
              </Typography>
            </Stack>
          ))}
          {/* {lvdts.map((v, i) => (
            <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '64px minmax(0, 1fr) 72px', alignItems: 'center', gap: 1 }}>
              <Stack direction='row' alignItems='center' spacing={0.75}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: lvdtColors[i] }} />
                <Typography variant='body2'>LVDT {i + 1}</Typography>
              </Stack>
              <Box sx={{ height: 6, borderRadius: 3, backgroundColor: 'background.dashboard', overflow: 'hidden' }}>
                <Box sx={{ height: 6, width: `${lvdtPercent(v)}%`, backgroundColor: lvdtColors[i] }} />
              </Box>
              <Typography variant='body2' sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(v, units.fmtLength)}</Typography>
            </Box>
          ))} */}
        </Stack>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
        <Box>
          <Typography variant='caption' color='text.secondary' component='div'>Rate ({selectedStep ? `step ${selectedStep.label}` : 'step'})</Typography>
          <Typography variant='body2' fontWeight={500}>{!rawSettlement && selectedStep?.rateIn != null ? `${units.fmtRate(selectedStep.rateIn)} ${units.rateUnit}` : '—'}</Typography>
        </Box>
        <Box>
          <Typography variant='caption' color='text.secondary' component='div'>Plate angle</Typography>
          <Tooltip arrow placement='top' title={plateAlert ? plateAlert.message : ''} describeChild>
            <Stack direction='row' alignItems='center' spacing={0.5} sx={{ width: 'fit-content', cursor: plateAlert ? 'help' : 'default' }}>
              {plateAlert && <plateAlert.Icon color={plateAlert.color} sx={{ fontSize: 16 }} titleAccess={plateAlert.label} />}
              <Typography variant='body2' fontWeight={plateAlert ? 600 : 500} color={plateAlert ? `${plateAlert.color}.main` : undefined}>
                {fmt(currentReadings?.plate_angle_deg, (v) => `${v.toFixed(2)}°`)}
              </Typography>
            </Stack>
          </Tooltip>
        </Box>
        <Box>
          <Typography variant='caption' color='text.secondary' component='div'>Strain gauges</Typography>
          <Typography variant='body2' fontWeight={500}>{loading ? '—' : `${activeStrainGauges || 0} active`}</Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default ReadingsPanel;
